import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { ITurnAroundTime } from 'wisestep-api';
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(calendar);
export const getDaysBetweenDates = (startDate: Date, endDate: Date) => {
  const date1 = dayjs(startDate);
  const date2 = dayjs(endDate);
  return date2.diff(date1, 'day');
};

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const currentMonthStartDate = () =>
  new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();

export const currentQuarterStartDate = () => {
  const now = new Date();
  const currentQuarter = Math.floor(now.getMonth() / 3);
  const quarterStartDate = new Date(now.getFullYear(), currentQuarter * 3, 1);
  return quarterStartDate.toISOString();
};

export const timeSince = (date: string) => dayjs().to(dayjs(date));

export const formatDateForTaskCard = (date: string) => {
  return dayjs(date).format('HH:mm, D MMMM YYYY');
};

export const formatDateInMonthAndYear = (date: string) => {
  return dayjs(date).format('MMMM YYYY');
};

export const formateDateInDateMonthYear = (date: string) => dayjs(date).format('DD MMM YYYY');

export const formateDateInDateMonth = (date: string) => dayjs(date).format('DD MMM');

export const formateDateInYear = (date: string) => dayjs(date).format('YYYY');

export const formatDateInDayMonthYearTime = (date: string) => {
  return dayjs(date).format('DD/MM/YYYY HH:mm:ss');
};

export const getTurnAroundTimeString = (turnAroundTime: ITurnAroundTime): string => {
  if (turnAroundTime.years > '0') {
    return `${turnAroundTime.years} Year(s)`;
  }
  if (turnAroundTime.months > '0') {
    return `${turnAroundTime.months} Month(s)`;
  }
  if (turnAroundTime.days > '0') {
    return `${turnAroundTime.days} Day(s)`;
  }
  if (turnAroundTime.hours > '0') {
    return `${turnAroundTime.hours} Hour(s)`;
  }
  if (turnAroundTime.minutes > '0') {
    return `${turnAroundTime.minutes} Minute(s)`;
  }
  if (turnAroundTime.seconds > '0') {
    return `${turnAroundTime.seconds} Second(s)`;
  }
  return '';
};

export const activityFormattedDate = (date: string): string =>
  dayjs(date).format('DD MMM dddd HH:mm');

export const formateDateForNewSubmission = (date: string): string =>
  dayjs(date).format('DD MMM YYYY HH:mm');

export const formattedDateISO = (date: string | Date): string => dayjs(date).toISOString();

export const formattedDateISOWithZeroTime = (date: string | Date): string =>
  dayjs(date).utc().startOf('day').format();

export const subtractDays = (value: number) => dayjs().subtract(value, 'day');

export const addDays = (value: number) => dayjs().add(value, 'day');

export const addHours = (value: number) => dayjs().add(value, 'hour');

export const getDateUsingFormat = (date: string, format: string) => dayjs(date).format(format);

export const getCurrentZoneAbbr = () => {
  const offset = new Date().getTimezoneOffset(),
    o = Math.abs(offset);
  return (
    (offset < 0 ? '+' : '-') +
    ('00' + Math.floor(o / 60)).slice(-2) +
    ':' +
    ('00' + (o % 60)).slice(-2)
  );
};

export const getCurrentZoneAbbrInHour = () => {
  const offset = new Date().getTimezoneOffset(),
    o = Math.abs(offset);
  return (offset < 0 ? '+' : '-') + ('00' + Math.floor(o / 60)).slice(-2);
};

export const getCurrentZoneAbbrInMinute = () => {
  const offset = new Date().getTimezoneOffset(),
    o = Math.abs(offset);
  return ('00' + (o % 60)).slice(-2);
};

export const getStandardTime = () =>
  new Date()?.toTimeString()?.match(new RegExp('[A-Z](?!.*[(])', 'g'))?.join('');

export const formattedDate = (date: string): string => dayjs(date).format('DD/MM/YYYY');

export const getDateDifferenceInDay = (date1: string, date2: string) => {
  const date = dayjs(date1);
  return date.diff(date2, 'day');
};

export const getFormatDate = (date: string): string => dayjs(date).format('DD MMMM ,YYYY');

export const getMonthAndYearInSameTimeZone = (dateString: string): string => {
  const [year, month] = dateString.split('-');
  return `${monthNames[parseInt(month) - 1]} ${year}`;
};

export const formatDateInDayMonthYear = (date: Date): string => {
  const dd = date.getDate();
  const mm = date.getMonth() + 1;
  const year = date.getFullYear();
  return (dd < 10 ? '0' + dd : dd) + '-' + (mm < 10 ? '0' + mm : mm) + '-' + year;
};

export const formatDateToMonthDayYearString = (date: Date) => {
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const year = date.getFullYear();
  const month = monthNames[date.getMonth()];
  const day = String(date.getDate()).padStart(2, '0');
  return `${month} ${day}, ${year}`;
};

export const currentDayOfYear = () => {
  dayjs.extend(dayOfYear);
  return dayjs(new Date()).dayOfYear();
};

dayjs.extend(quarterOfYear);

export const currentDayInQuarter = () => {
  const currentDate = dayjs(new Date());
  const startDate = currentDate.startOf('quarter');
  return currentDate.diff(startDate, 'day') + 1;
};

export const lastQuarterStart = dayjs().subtract(1, 'quarter').startOf('quarter');

export const lastQuarterEnd = dayjs().subtract(1, 'quarter').endOf('quarter');

export const getQuarterMonths = (date: string): string[] => {
  const parsedDate = dayjs(date, 'YYYY-MM-DD');

  const quarterStart = parsedDate.startOf('quarter');
  const quarterEnd = parsedDate.endOf('quarter');

  const quarterMonths = [];

  let currentMonth = quarterStart.clone();
  while (currentMonth.isBefore(quarterEnd) || currentMonth.isSame(quarterEnd, 'month')) {
    quarterMonths.push(currentMonth.format('MMM'));
    currentMonth = currentMonth.add(1, 'month');
  }

  return quarterMonths;
};

export const startOfWeek = dayjs().startOf('week').add(1, 'day'); // Monday 00:00

export const getStartAndEndTime = (date: Date, startTime: Date, endTime: Date, tz: string) => {
  const dateString = dayjs(date).format('YYYY-MM-DD');
  const from = dayjs.tz(`${dateString} ${dayjs(startTime).format('HH:mm:ss')}`, tz).toDate();
  const to = dayjs.tz(`${dateString} ${dayjs(endTime).format('HH:mm:ss')}`, tz).toDate();
  return { from, to };
};
export const getLastNDaysAgo = (numberOfDays: number) =>
  dayjs().subtract(numberOfDays, 'day').toDate();

export const getStartingDay = (unit: 'year' | 'month' | 'day' | 'quarter') =>
  dayjs().startOf(unit).toDate();

export const getYesterdaySameTime = () => dayjs().subtract(1, 'day').toDate();

export const extractDateFromISODate = (dateTime: string) =>
  dateTime.includes('T') ? dateTime.split('T')[0] : dateTime;

export const getLastMonthStart = dayjs().startOf('month').subtract(1, 'month');
export const getLastMonthEnd = dayjs().endOf('month').subtract(1, 'month');
export const getLastWeekStart = dayjs().startOf('week').subtract(1, 'week');
export const getLastWeekEnd = dayjs().endOf('week').subtract(1, 'week');
export const getLastYearStart = dayjs().startOf('year').subtract(1, 'year');
export const getLastYearEnd = dayjs().endOf('year').subtract(1, 'year');
export const getDateDifferenceInMonths = (date1: string, date2: string) => {
  const date = dayjs(date1);
  return date.diff(date2, 'month');
};

export const getHoursOld = (dateString: string): number => {
  const givenDate: Date = new Date(dateString);
  const currentDate: Date = new Date();
  const differenceInMilliseconds: number = currentDate.getTime() - givenDate.getTime();
  const hoursOld: number = differenceInMilliseconds / (1000 * 60 * 60);
  return hoursOld;
};

const dateFormats = (withTime?: boolean) => {
  if (withTime) {
    return {
      sameDay: '[Today at] h:mm A', // The same day ( Today at 2:30 AM )
      nextDay: '[Tomorrow at] h:mm A', // The next day ( Tomorrow at 2:30 AM )
      nextWeek: 'dddd [at] h:mm A', // The next week ( Sunday at 2:30 AM )
      lastDay: '[Yesterday at] h:mm A', // The day before ( Yesterday at 2:30 AM )
      lastWeek: '[Last] dddd [at] h:mm A', // Last week ( Last Monday at 2:30 AM )
      sameElse: 'ddd,D MMMM, YYYY h:mm A',
    };
  }
  return {
    sameDay: '[Today ,] D MMMM', // The same day ( Today at 2:30 AM )
    nextDay: '[Tomorrow]', // The next day ( Tomorrow at 2:30 AM )
    nextWeek: 'dddd', // The next week ( Sunday at 2:30 AM )
    lastDay: '[Yesterday ,]', // The day before ( Yesterday at 2:30 AM )
    lastWeek: '[Last] dddd', // Last week ( Last Monday at 2:30 AM )
    sameElse: 'ddd,D MMMM, YYYY', // Everything else ( 7/10/2011 )
  };
};
export const timeCalendarSince = (date: Date, withTime?: boolean) =>
  dayjs(date).calendar(dayjs(), dateFormats(withTime));

export const formatDayToTime = (date: string) => {
  return dayjs(date).format('HH:mm');
};
